import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    Project, ProjectBudgetVsActual, ProjectLedgerPage, ProjectOption, ProjectPl, ProjectPlSummary, ProjectRequest,
    ProjectStatus, ProjectStatusRequest,
} from '../interfaces/project';
import { saveBlob } from '../utils/saveBlob';
import { parseContentDispositionFilename } from '../utils/contentDisposition';

const institution = () => AuthService.getSelectedInstitution()!;

export interface ProjectListParams {
    status?: ProjectStatus;
    customerId?: string;
    managerId?: string;
    search?: string;
    page?: number;
    size?: number;
}

export interface Page<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

const download = async (url: string, params: Record<string, unknown>, fallback: string) => {
    const response = await axios.get(url, { params, responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] ?? '';
    saveBlob(new Blob([response.data]), parseContentDispositionFilename(disposition, fallback));
};

/** P1-A projects and project costing. */
export const ProjectService = {
    list: async (p: ProjectListParams = {}): Promise<Page<Project>> =>
        (await axios.get(ENDPOINTS.PROJECTS.LIST(institution()), { params: p })).data,
    mine: async (): Promise<Project[]> => (await axios.get(ENDPOINTS.PROJECTS.MINE(institution()))).data,
    /** The posting picker (any member; filtered server-side by D-P1-3). */
    options: async (): Promise<ProjectOption[]> => (await axios.get(ENDPOINTS.PROJECTS.OPTIONS(institution()))).data,
    /** P1-A tail: the invoice picker — the options narrowed to this customer's projects and those with no customer (uncached). */
    optionsForCustomer: async (customerId: string): Promise<ProjectOption[]> =>
        (await axios.get(ENDPOINTS.PROJECTS.OPTIONS(institution()), { params: { customerId } })).data,
    get: async (id: string): Promise<Project> => (await axios.get(ENDPOINTS.PROJECTS.BY_ID(institution(), id))).data,
    create: async (body: ProjectRequest): Promise<Project> =>
        (await axios.post(ENDPOINTS.PROJECTS.LIST(institution()), body)).data,
    update: async (id: string, body: ProjectRequest): Promise<Project> =>
        (await axios.put(ENDPOINTS.PROJECTS.BY_ID(institution(), id), body)).data,
    changeStatus: async (id: string, body: ProjectStatusRequest): Promise<Project> =>
        (await axios.post(ENDPOINTS.PROJECTS.STATUS(institution(), id), body)).data,
    remove: async (id: string): Promise<void> => {
        await axios.delete(ENDPOINTS.PROJECTS.BY_ID(institution(), id));
    },
    projectPl: async (p: { from: string; to: string; status?: ProjectStatus }): Promise<ProjectPlSummary> =>
        (await axios.get(ENDPOINTS.PROJECTS.PROJECT_PL(institution()), { params: p })).data,
    /** Downloads exactly the filters passed in (callers pass the APPLIED ones). */
    downloadProjectPl: async (p: { from: string; to: string; status?: ProjectStatus }, format: 'pdf' | 'xlsx' = 'xlsx') =>
        download(ENDPOINTS.PROJECTS.PROJECT_PL_EXPORT(institution()), { ...p, format }, `project-pl-${p.to}.${format}`),
    pl: async (id: string, p: { from?: string; to?: string } = {}): Promise<ProjectPl> =>
        (await axios.get(ENDPOINTS.PROJECTS.PL(institution(), id), { params: p })).data,
    budgetVsActual: async (id: string, p: { from?: string; to?: string; versionId?: string } = {}): Promise<ProjectBudgetVsActual> =>
        (await axios.get(ENDPOINTS.PROJECTS.BUDGET_VS_ACTUAL(institution(), id), { params: p })).data,
    ledger: async (id: string, p: { from?: string; to?: string; accountId?: string; page?: number; size?: number } = {}): Promise<ProjectLedgerPage> =>
        (await axios.get(ENDPOINTS.PROJECTS.LEDGER(institution(), id), { params: p })).data,
    downloadLedger: async (id: string, p: { from?: string; to?: string; accountId?: string }, format: 'pdf' | 'xlsx' = 'xlsx') =>
        download(ENDPOINTS.PROJECTS.LEDGER_EXPORT(institution(), id), { ...p, format }, `project-ledger.${format}`),
    tasks: async (id: string, p: { page?: number; size?: number } = {}): Promise<Page<{
        id: string; title: string; assigneeName?: string; status: string; dueDate?: string | null; priority: string;
    }>> => (await axios.get(ENDPOINTS.PROJECTS.TASKS(institution(), id), { params: p })).data,
};
